"use client";

import { useState, useRef, useEffect, useMemo } from "react";

interface MultiselectDropdownProps {
    options: Set<string>;
    selectedOptions: Set<string>;
    onSelectionChange: (selected: Set<string>) => void;
    placeholder?: string;
    className?: string;
}

export function MultiselectDropdown({
    options,
    selectedOptions,
    onSelectionChange,
    className = "",
}: MultiselectDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedArray = Array.from(selectedOptions);

    const filteredOptions = useMemo(() => {
        const arr = Array.from(options);

        // Sorts and filters by search term
        arr.sort((a, b) => {
            if (searchTerm.trim() === "") {
                return a.localeCompare(b);
            } else {
                const lowerSearch = searchTerm.toLowerCase();
                const aIncludes = a.toLowerCase().includes(lowerSearch);
                const bIncludes = b.toLowerCase().includes(lowerSearch);

                if (aIncludes && !bIncludes) return -1;
                if (!aIncludes && bIncludes) return 1;

                return a.localeCompare(b);
            }
        });

        if (searchTerm.trim() !== "") {
            return arr.filter((opt) =>
                opt.toLowerCase().includes(searchTerm.toLowerCase()),
            );
        }

        return arr;
    }, [options, searchTerm]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleOption = (option: string) => {
        const newSelected = new Set(selectedOptions);
        newSelected.has(option)
            ? newSelected.delete(option)
            : newSelected.add(option);
        onSelectionChange(newSelected);
    };

    const clearAll = () => onSelectionChange(new Set());

    return (
        <div ref={dropdownRef} className={`relative ${className}`}>
            {/* Trigger / Search */}
            <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Produkt Filter"
                className={`w-[120px] h-[42px] overflow-y-auto overflow-x-none border ${
                    isOpen ? "" : "rounded-b-md"
                } rounded-t-md px-2 py-1 text-left bg-white`}
                onClick={() => setIsOpen(true)}
            />

            {/* Dropdown */}
            {isOpen && (
                <div
                    className={`absolute z-10 w-[120px] overflow-y-auto overflow-x-none border-b border-l border-r rounded-b-md bg-white shadow max-h-48`}
                >
                    {/* Clear All */}
                    {selectedArray.length > 0 && (
                        <button
                            onClick={clearAll}
                            className="w-full text-left px-2 py-2 cursor-pointer hover:bg-gray-100 text-blue-600"
                        >
                            Tøm
                        </button>
                    )}

                    {filteredOptions.length === 0 ? (
                        <div className="text-gray-500 px-2 py-1 text-sm">
                            Ingen treff
                        </div>
                    ) : (
                        filteredOptions.map((option) => (
                            <div
                                key={option}
                                onClick={() => toggleOption(option)}
                                className={`px-3 py-2 w-[120px] cursor-pointer hover:bg-gray-100 ${
                                    selectedOptions.has(option)
                                        ? "bg-blue-100 font-medium"
                                        : ""
                                }`}
                            >
                                {option}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
